import { gql } from "@apollo/client";
import {
    createPagePagingActions,
    SortDirection,
    Table as AdminTable,
    TableQuery,
    useTableQuery,
    useTableQueryPaging,
    useTableQuerySort,
} from "@comet/admin";
import { ContentWrapper, useMemoizedTableVariables } from "@src/components";
import { urls } from "@src/domain/urls";
import { type GQLTrapAlarmsUserAdminQuery, type GQLTrapAlarmsUserAdminQueryVariables, type GQLUserAdminQuery } from "@src/graphql.generated";
import { type FC, type PropsWithChildren, useCallback } from "react";
import { useIntl } from "react-intl";
import { Link } from "react-router-dom";
import styled from "styled-components";

const trapAlarmsUserQuery = gql`
    query TrapAlarmsUserAdmin($userId: ID!, $page: Int, $size: Int, $sort: SortInput) {
        trapAlarmsAdmin(userId: $userId, page: $page, size: $size, sort: $sort) {
            nodes {
                id
                name
                phone
                pin
            }
            totalCount
            nextPage
            previousPage
            totalPages
        }
    }
`;

const TrapAlarmTable: FC<PropsWithChildren<GQLUserAdminQuery>> = ({ userAdmin }) => {
    const pagingApi = useTableQueryPaging(1);
    const sortApi = useTableQuerySort({ columnName: "createdAt", direction: SortDirection.DESC });
    const intl = useIntl();
    const addAdditionalQueryVariablesFn = useCallback(() => ({ userId: userAdmin.id, size: 5 }), [userAdmin.id]);

    const memoizedVariables = useMemoizedTableVariables<GQLTrapAlarmsUserAdminQueryVariables>({ pagingApi, sortApi, addAdditionalQueryVariablesFn });

    const { tableData, loading, error, api } = useTableQuery<GQLTrapAlarmsUserAdminQuery, GQLTrapAlarmsUserAdminQueryVariables>()(
        trapAlarmsUserQuery,
        {
            resolveTableData: ({ trapAlarmsAdmin }) => ({
                data: trapAlarmsAdmin.nodes,
                totalCount: trapAlarmsAdmin.totalCount,
                pagingInfo: createPagePagingActions(pagingApi, trapAlarmsAdmin),
            }),
            variables: memoizedVariables,
        },
    );

    return (
        <ContentWrapper title={intl.formatMessage({ id: "reviermanager.page.trapAlarm", defaultMessage: "Fallenalarme" })}>
            <TableQuery api={api} loading={loading} error={error}>
                {tableData && (
                    <AdminTable
                        {...tableData}
                        rowName={intl.formatMessage(
                            {
                                id: "reviermanager.pages.trapAlarms",
                                defaultMessage: "{count, plural, one {Fallenalarm} other {Fallenalarme}}",
                            },
                            { count: tableData.totalCount },
                        )}
                        columns={[
                            {
                                name: "name",
                                sortable: true,
                                header: intl.formatMessage({ id: "reviermanager.trapAlarm.name", defaultMessage: "Name" }),
                                render: ({ name, id }) => <StyledLink to={`${urls.trapAlarms}/${id}/edit`}>{name}</StyledLink>,
                                renderExcel: ({ name }) => name,
                            },
                            {
                                name: "phone",
                                header: intl.formatMessage({ id: "reviermanager.trapAlarm.phone", defaultMessage: "Telefon" }),
                                render: ({ phone }) => phone,
                            },
                            {
                                name: "pin",
                                header: intl.formatMessage({ id: "reviermanager.trapAlarm.pin", defaultMessage: "Pin" }),
                                render: ({ pin }) => pin,
                            },
                        ]}
                    />
                )}
            </TableQuery>
        </ContentWrapper>
    );
};

const StyledLink = styled(Link)`
    color: black;
    font-size: 16px;
`;

export default TrapAlarmTable;
